import styles from './style/layout.module.scss';
import InnerLayout from '../Layout/InnerLayout';
import AlertSwitch from '../Utilities/AlertSwitch';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Skeleton from 'react-loading-skeleton';
import { ClipLoader } from 'react-spinners';
import {
  getDetail,
  removeSuccess,
  removePostError,
  selectMenu,
  updateDetail,
  updateDetailSetting,
  updateNeedAiSeat,
  updateDescript,
  postRepresentativeUser,
  getEnterpriseSetting,
  updateEnterpriseSetting,
} from '../../store/slice/manager/menu';
import { getServiceName } from '../../constant/SERVICE_NAME_MAP';
import { emailRegex } from '../../utils';

import Radio from '../Utilities/Radio';
import SnackBar from '../Utilities/Alert';
import Toast from '../Utilities/Toast';
import Button from '../Utilities/Button';
import TextField from '../Utilities/TextField';

import { MdClear, MdDone, MdAdd } from 'react-icons/md';

const Dashboard = ({ serviceId: propServiceId, isModal = false }) => {
  const params = useParams();
  // 一般路由使用 params.id；以 modal 開啟時改用傳入的 serviceId。
  const serviceId = propServiceId != null ? String(propServiceId) : params.id;
  const dispatch = useDispatch();
  const [isSwitch, setIsSwitch] = useState(false);
  const [startService, setStartService] = useState(false);
  const [needAiSeat, setNeedAiSeat] = useState(false);
  const [descripts, setDescripts] = useState(['']); // 服務說明，可多筆；至少保留一列空輸入
  const [descriptSaveStatus, setDescriptSaveStatus] = useState(''); // '' | 'success' | 'error'：按鈕旁行內提示
  const [type, setType] = useState('');
  const [isRepresentativeUserSetting, setIsRepresentativeUserSetting] = useState(false);
  const [representativeUserEmail, setRepresentativeUserEmail] = useState('');
  const [representativeUserEmailError, setRepresentativeUserEmailError] = useState(false);

  // toast status
  const [status, setStatus] = useState('');

  const { detail, success, updateLoading, postError, postErrorMsg, detailServiceId } = useSelector(selectMenu);

  // menu slice 為全域狀態，切換到不同 service 時 detail 仍殘留前一個 service 的資料。
  // 必須確認 detail 所屬 service 與目前 serviceId 相符才算就緒，否則先顯示 loading 圈圈，
  // 避免先閃出前一個 service 的服務設定再突然切換。
  const detailReady = !!detail && String(detailServiceId) === String(serviceId);

  useEffect(() => {
    if (serviceId === '0') {
      dispatch(getEnterpriseSetting());
    } else {
      dispatch(getDetail({ service: serviceId }));
    }
  }, [dispatch, serviceId]);

  useEffect(() => {
    if (detail) {
      if (serviceId === '0') {
        setType(detail.enterprise_register_email_verify);
      } else {
        setType(detail.activate_mode);
        setNeedAiSeat(!!detail.need_ai_seat);
        setDescripts(Array.isArray(detail.descript) && detail.descript.length ? detail.descript : ['']);
      }
      setIsSwitch(detail.status);
    }
  }, [detail, serviceId]);

  useEffect(() => {
    if (success || postError) {
      if (serviceId === '0') {
        dispatch(getEnterpriseSetting());
      } else {
        dispatch(getDetail({ service: serviceId }));
      }

      setIsRepresentativeUserSetting(false);
      setRepresentativeUserEmail('');
    }

    setTimeout(() => {
      dispatch(removeSuccess());
    }, 1200);
    setTimeout(() => {
      setStatus('');
      dispatch(removePostError());
    }, 1500);
  }, [dispatch, success, serviceId, status, postError]);

  const handleSuccess = () => {
    const postObj = {
      service: detail.id,
      activate_mode: type,
      status: !isSwitch,
    };
    dispatch(updateDetail(postObj));
    setStatus('serviceStatus');
    setStartService(false);
  };

  const changeMode = (mode) => {
    const postObj = {
      service: detail.id,
      activate_mode: mode,
      status: isSwitch,
    };
    dispatch(updateDetailSetting(postObj));
    setType(mode);
  };

  const changeNeedAiSeat = () => {
    const next = !needAiSeat;
    setNeedAiSeat(next); // 樂觀更新，與開通設定切換一致
    dispatch(updateNeedAiSeat({ service: detail.id, need_ai_seat: next }));
  };

  const changeDescript = (index, value) => {
    setDescriptSaveStatus(''); // 一旦重新編輯就收起提示
    setDescripts((prev) => prev.map((d, i) => (i === index ? value : d)));
  };

  const addDescript = () => {
    setDescriptSaveStatus('');
    setDescripts((prev) => [...prev, '']);
  };

  const removeDescript = (index) => {
    setDescriptSaveStatus('');
    // 至少保留一列空輸入，避免整個區塊消失
    setDescripts((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : ['']));
  };

  const saveDescript = () => {
    // 去除前後空白與空白列；全空則存 null（清空）
    const cleaned = descripts.map((d) => d.trim()).filter(Boolean);
    dispatch(updateDescript({ service: detail.id, descript: cleaned.length ? cleaned : null })).then((res) => {
      if (res?.payload?.msg === 'success') {
        setDescripts(cleaned.length ? cleaned : ['']); // 讓畫面與實際存檔內容一致（去掉空白列）
        setDescriptSaveStatus('success');
      } else {
        setDescriptSaveStatus('error');
      }
      setTimeout(() => setDescriptSaveStatus(''), 2500);
    });
  };

  const changeEnterpriseMode = (mode) => {
    const postObj = {
      enterprise_register_email_verify: mode,
    };
    dispatch(updateEnterpriseSetting(postObj));
    setType(mode);
  };

  const handleSetRepresentativeUser = () => {
    if (representativeUserEmail.match(emailRegex)) {
      setRepresentativeUserEmailError(false);
      dispatch(
        postRepresentativeUser({
          service: detail.id,
          user_email: representativeUserEmail.replace('+', '%2B'),
        })
      );
      setStatus('representUser');
    } else {
      setRepresentativeUserEmailError(true);
    }
  };

  useEffect(() => {
    if (representativeUserEmailError) {
      if (representativeUserEmail.match(emailRegex)) {
        setRepresentativeUserEmailError(!representativeUserEmailError);
      }
    }
  }, [representativeUserEmailError, representativeUserEmail]);

  const settingsBody = (
    <div className={styles.dashboardSettings}>
      {serviceId !== '0' && (
        <>
          <div className={styles.dashboardFlex}>
            <div className={styles.dashboardSettingsTitle}>服務啟用狀態</div>
            <div className={styles.dashboardSettingssContent}>
              <AlertSwitch isChecked={isSwitch} onChange={() => setStartService(true)} />
              <div className={styles.startService}>{isSwitch ? '啟用中' : '停用中'}</div>
            </div>
          </div>
          <div className={styles.dashboardFlex}>
            <div className={styles.dashboardSettingsTitle}>啟用中企業帳號總數</div>
            <div className={styles.dashboardSettingssContent}>{detail?.enterprises_using_service_count}</div>
          </div>
          <div className={styles.dashboardFlex}>
            <div className={styles.dashboardSettingsTitle}>總使用人數</div>
            <div className={styles.dashboardSettingssContent}>{detail?.users_using_service_count}</div>
          </div>
        </>
      )}

      {serviceId === '0' && (
        <>
          <div className={styles.dashboardFlex}>
            <div className={styles.dashboardSettingsTitle}>已號開企業帳號總數</div>
            <div className={styles.dashboardSettingssContent}>{detail?.email_verified}</div>
          </div>
          <div className={styles.dashboardFlex}>
            <div className={styles.dashboardSettingsTitle}>總使企業帳號數</div>
            <div className={styles.dashboardSettingssContent}>{detail?.total_enterprise}</div>
          </div>
        </>
      )}

            <div className={styles.dashboardFlex} style={{ display: 'none' }}>
              <div className={styles.dashboardSettingsTitle}>服務負責人設定</div>
              <div className={styles.dashboardSettingssContent}></div>
            </div>

            {serviceId === '0' ? (
              <div className={styles.dashboardFlex}>
                <div className={styles.dashboardSettingsTitle}>企業帳號開通設定</div>
                <div className={styles.dashboardSettingssContent}>
                  <div className={styles.RadioMargin}>
                    <Radio value={type === 'center' ? true : false} onChange={() => changeEnterpriseMode('center')}>
                      僅由管理者驗證 (不發送驗證信)
                    </Radio>
                  </div>
                  <Radio value={type === 'email' ? true : false} onChange={() => changeEnterpriseMode('email')}>
                    由管理者或認證信件驗證
                  </Radio>
                </div>
              </div>
            ) : (
              <div className={styles.dashboardFlex}>
                <div className={styles.dashboardSettingsTitle}>開通設定</div>
                <div className={styles.dashboardSettingssContent}>
                  <div className={styles.RadioMargin}>
                    <Radio value={type === 'center' ? true : false} onChange={() => changeMode('center')}>
                      由管理者開通
                    </Radio>
                  </div>
                  <Radio value={type === 'enterprise' ? true : false} onChange={() => changeMode('enterprise')}>
                    由企業主付款後開通
                  </Radio>
                </div>
              </div>
            )}

            {serviceId !== '0' && (
              <div className={styles.dashboardFlex}>
                <div className={styles.dashboardSettingsTitle}>需 AI 席位</div>
                <div className={styles.dashboardSettingssContent}>
                  <AlertSwitch isChecked={needAiSeat} onChange={changeNeedAiSeat} />
                  <div className={styles.startService}>{needAiSeat ? '需要' : '不需要'}</div>
                </div>
              </div>
            )}

            {serviceId !== '0' && (
              <div className={styles.dashboardFlex}>
                <div className={styles.dashboardSettingsTitle}>服務說明</div>
                <div className={styles.dashboardSettingssContent}>
                  <div className={styles.descriptEditor}>
                    {descripts.map((d, index) => (
                      <div className={styles.descriptRow} key={index}>
                        <TextField
                          placeholder={'請輸入服務說明'}
                          value={d}
                          onChange={(value) => changeDescript(index, value)}
                          width={'320px'}
                        />
                        <Button
                          type={'secondary'}
                          icon={<MdClear />}
                          color={'#999'}
                          small
                          disabled={descripts.length === 1 && !descripts[0]}
                          onClick={() => removeDescript(index)}
                        />
                      </div>
                    ))}
                    <div className={styles.descriptActions}>
                      <Button type={'tertiary'} title={'新增說明'} icon={<MdAdd />} onClick={addDescript} />
                      <Button type={'primary'} title={'儲存'} color={'#34c759'} onClick={saveDescript} />
                      {descriptSaveStatus === 'success' && <span className={styles.descriptSavedHint}>已儲存</span>}
                      {descriptSaveStatus === 'error' && <span className={styles.descriptErrorHint}>儲存失敗，請稍後再試</span>}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {serviceId !== '0' && (
              <div className={styles.dashboardFlex}>
                <div className={styles.dashboardSettingsTitle}>負責人</div>
                <div className={styles.dashboardSettingssContent}>
                  {detail?.representative_user && !isRepresentativeUserSetting && !updateLoading ? (
                    <div className={styles.representativeUserDetail}>
                      {detail?.representative_user?.email}
                      <Button title={'設定'} type={'tertiary'} onClick={() => setIsRepresentativeUserSetting(true)} />
                    </div>
                  ) : !detail?.representative_user && !isRepresentativeUserSetting && !updateLoading ? (
                    <div className={styles.representativeNonSettingOuter}>
                      <div className={styles.nonSettingTitle}>尚未設定</div>
                      <Button title={'設定'} type={'tertiary'} onClick={() => setIsRepresentativeUserSetting(true)} />
                    </div>
                  ) : updateLoading ? (
                    <>
                      <Skeleton height={40} width={297} />
                    </>
                  ) : (
                    <div className={styles.userSettingInputOuter}>
                      <TextField
                        placeholder={'請輸入負責人的電子郵件'}
                        onChange={(e) => setRepresentativeUserEmail(e)}
                        value={representativeUserEmail ? representativeUserEmail : ''}
                        width={320}
                        error={representativeUserEmailError}
                        disabled={updateLoading}
                      />
                      {representativeUserEmailError && <div className={styles.errorMsg}>請輸入正確的格式。</div>}
                      <div className={styles.btnGroup}>
                        <Button
                          type={'secondary'}
                          icon={<MdClear />}
                          color={'#999'}
                          onClick={() => {
                            setRepresentativeUserEmail('');
                            setIsRepresentativeUserSetting(!isRepresentativeUserSetting);
                            setRepresentativeUserEmailError(false);
                          }}
                          disabled={updateLoading}
                        />
                        <Button
                          type={'secondary'}
                          icon={<MdDone />}
                          disabled={!representativeUserEmail || updateLoading}
                          color={'#34c759'}
                          onClick={handleSetRepresentativeUser}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <SnackBar
              isOpen={startService}
              onConfirm={handleSuccess}
              onClose={() => setStartService(false)}
              rightButtonText={isSwitch ? '停用' : '啟用'}
              leftButtonText='取消'
              mainTitle={
                isSwitch
                  ? `停用「${getServiceName(detail?.name)}於KEYSTONE系統」`
                  : `啟用「${getServiceName(detail?.name)}於KEYSTONE系統」`
              }
            />

            <Toast
              show={success || postError}
              title={
                postError
                  ? postErrorMsg === 'Cannot enable the service!'
                    ? `「${getServiceName(detail?.name)}」尚未發佈可用方案，無法啟用服務`
                    : postErrorMsg === 'Cannot disable the service!'
                    ? `「${getServiceName(detail?.name)}」尚有企業帳號使用中，無法停用服務`
                    : `「${getServiceName(detail?.name)}」無法變更服務啟用狀態`
                  : status === 'serviceStatus' && isSwitch
                  ? `已啟用${getServiceName(detail?.name)}於KEYSTONE系統`
                  : status === 'serviceStatus' && !isSwitch
                  ? `已停用${getServiceName(detail?.name)}於KEYSTONE系統`
                  : status === 'representUser'
                  ? `服務負責人設定完成`
                  : ''
              }
              subtitle={getServiceName(detail?.name)}
              error={false}
            />
          </div>
  );

  const title = serviceId === '0' ? '企業帳號' : detailReady && detail?.name ? getServiceName(detail?.name) : '服務設定';

  const loadingSpinner = (
    <div className={styles.loadingWrapper}>
      <ClipLoader />
    </div>
  );

  // modal 模式只渲染設定內容本體；標題/關閉/分頁由外層 ServiceEditModal 負責。
  if (isModal) {
    return <div className={styles.serviceSettingModal}>{detailReady ? settingsBody : loadingSpinner}</div>;
  }

  return (
    <InnerLayout>
      <div className={styles.dashboard}>
        <div className={styles.dashboardRight}>
          <div className={styles.dashboardTitle}>{title}</div>
          {detailReady ? settingsBody : loadingSpinner}
        </div>
      </div>
    </InnerLayout>
  );
};

export default Dashboard;
